import { useState, useEffect } from "react";

export interface UseDebounceOptions<T> {
    onUpdate?: (value: T) => void;
}

export function useDebounce<T>(
    value: T,
    delay: number,
    { onUpdate }: UseDebounceOptions<T> = {}
) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const [isInitial, setIsInitial] = useState(true);
    const [handler, setHandler] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (handler) {
            clearTimeout(handler);
        }
        const timeout = setTimeout(() => {
            setDebouncedValue(value);
            if (isInitial) {
                setIsInitial(false);
                return;
            }
            onUpdate?.(value);
        }, delay);
        setHandler(timeout);
        return () => {
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, delay]);

    // Cancel function to clear the timeout
    const cancel = () => {
        if (handler) {
            clearTimeout(handler);
            setHandler(null);
        }
    };

    return { value: debouncedValue, cancel };
}
